import { useSelector } from "react-redux";
import { useMemo, useCallback } from "react";

const ACTIVE_STATUSES = ["active", "trialing"];

/**
 * Custom hook for managing and checking restaurant subscriptions
 * Provides methods to check if a module is active and get subscription details
 */
export function useSubscription() {
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const restaurants = useSelector((state) => state.user.restaurants);
  const controlledRestaurantId = useSelector(
    (state) => state.admin.controlledRestaurantId
  );

  // Get the current restaurant (either controlled or owned)
  const currentRestaurant = useMemo(() => {
    const id = controlledRestaurantId || restaurantId;
    return restaurants?.find((r) => r.restaurantId === id);
  }, [restaurants, restaurantId, controlledRestaurantId]);

  // Get all subscriptions for current restaurant
  const subscriptions = useMemo(() => {
    return currentRestaurant?.subscriptions || [];
  }, [currentRestaurant]);

  /**
   * Check if the restaurant has an active subscription for a specific module
   * @param {string} moduleName - The name of the module to check (e.g., "tables", "booking")
   * @returns {boolean} - True if the module subscription is active or trialing
   */
  const hasActiveModule = useCallback(
    (moduleName) => {
      return subscriptions.some(
        (s) =>
          s.module?.name === moduleName && ACTIVE_STATUSES.includes(s.status)
      );
    },
    [subscriptions]
  );

  /**
   * Get the active subscription for a specific module
   * @param {string} moduleName - The name of the module
   * @returns {Object|undefined} - The subscription object or undefined if not found
   */
  const getSubscription = useCallback(
    (moduleName) => {
      return subscriptions.find(
        (s) =>
          s.module?.name === moduleName && ACTIVE_STATUSES.includes(s.status)
      );
    },
    [subscriptions]
  );

  /**
   * Get all active subscriptions
   * @returns {Array} - Array of active subscriptions
   */
  const activeSubscriptions = useMemo(() => {
    return subscriptions.filter((s) => ACTIVE_STATUSES.includes(s.status));
  }, [subscriptions]);

  return {
    subscriptions,
    activeSubscriptions,
    hasActiveModule,
    getSubscription,
    currentRestaurant,
  };
}

export default useSubscription;
